/**
 * streakEngine.js — Pure streak logic. Zero React.
 *
 * Tracks check-in streaks (user opened the app) and budget compliance.
 * All dates are stored as YYYY-MM-DD strings for easy comparison.
 *
 * State shape (stored in s.streak):
 * {
 *   current: number,       // current consecutive check-in days
 *   best: number,          // all-time best streak
 *   lastDate: string|null, // last check-in date "YYYY-MM-DD"
 *   budgetStreak: number,  // consecutive check-ins where budget was met
 *   history: string[],     // last 30 check-in dates for the heatmap
 * }
 */

/** Default streak state for new users */
export const STREAK_DEFAULTS = {
  current: 0,
  best: 0,
  lastDate: null,
  budgetStreak: 0,
  history: [],
};

/** Get today's date as YYYY-MM-DD in local time */
export function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Get yesterday's date as YYYY-MM-DD in local time */
function yesterdayStr() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Process a check-in. Call this once per app load.
 *
 * @param {object} streak - current streak state (or STREAK_DEFAULTS)
 * @param {boolean} budgetMet - true if all budget categories are at or under budget
 * @returns {object} - updated streak state (or null if no change needed)
 */
export function processCheckIn(streak, budgetMet) {
  const s = streak || STREAK_DEFAULTS;
  const today = todayStr();

  // Already checked in today — no change
  if (s.lastDate === today) return null;

  const yesterday = yesterdayStr();
  const isConsecutive = s.lastDate === yesterday;

  const newCurrent = isConsecutive ? s.current + 1 : 1;
  const newBest = Math.max(s.best, newCurrent);
  const newBudgetStreak = budgetMet
    ? (isConsecutive ? s.budgetStreak + 1 : 1)
    : 0;

  // Keep last 30 days of history
  const newHistory = [...(s.history || []), today].slice(-30);

  return {
    current: newCurrent,
    best: newBest,
    lastDate: today,
    budgetStreak: newBudgetStreak,
    history: newHistory,
  };
}

/**
 * Get a motivational message based on streak state.
 *
 * @param {object} streak - current streak state
 * @returns {string} - contextual message
 */
export function getStreakMessage(streak) {
  const s = streak || STREAK_DEFAULTS;

  if (s.current === 0) return "Start your streak today.";
  if (s.current === 1) return "Day 1. Every empire starts here.";
  if (s.current < 3) return "Building momentum. Keep it going.";
  if (s.current < 7) return "Strong week. Your wealth streak survives.";
  if (s.current < 14) return "You're locked in. Consistency compounds.";
  if (s.current < 30) return "Unstoppable. Most people quit by now.";
  if (s.current < 60) return `${s.current} days. This is how wealth is built.`;
  if (s.current < 100) return "Elite consistency. Your future self thanks you.";
  return `${s.current}-day streak. You're in the 1%.`;
}

/**
 * Get streak tier for visual treatment.
 *
 * @param {number} current - current streak count
 * @returns {{ tier: string, emoji: string }}
 */
export function getStreakTier(current) {
  if (current >= 100) return { tier: "legendary", emoji: "💎" };
  if (current >= 60) return { tier: "elite", emoji: "🔥" };
  if (current >= 30) return { tier: "dedicated", emoji: "⚡" };
  if (current >= 14) return { tier: "committed", emoji: "✨" };
  if (current >= 7) return { tier: "building", emoji: "🌱" };
  if (current >= 1) return { tier: "starting", emoji: "🚀" };
  return { tier: "none", emoji: "○" };
}
